import { useState } from 'react'
import {
  Mtds,
  ScreenHeader,
  TopBar,
  AppLayout,
  LabReceptionAddressesSection,
  SaveError,
  useAuth,
  UserIcon
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import {
  useAddLabNotificationEmailMutation,
  useCurrentUserQuery,
  useGetLabNotificationEmailsQuery,
  useRemoveLabNotificationEmailMutation
} from '../hooks/useLabNotificationEmails'

const SettingsScreen = () => {
  const { accountName, logout } = useAuth()
  const [emailInput, setEmailInput] = useState('')
  const { data: notificationEmails } = useGetLabNotificationEmailsQuery()
  const addEmail = useAddLabNotificationEmailMutation()
  const removeEmail = useRemoveLabNotificationEmailMutation()
  const { data: currentUser } = useCurrentUserQuery()

  const emails = notificationEmails ?? []
  const canAddMore = emails.length < 5

  const handleAddEmail = () => {
    if (!emailInput.trim()) return
    addEmail.mutate(emailInput.trim(), {
      onSuccess: () => setEmailInput('')
    })
  }

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title='User'
          showNotificationBell={true}
          isProvePlan={false}
        />
      }
    >
      <Mtds.Prose>
        <ScreenHeader
          title='Settings'
          description={
            <p>Here you can change settings to customize your experience.</p>
          }
        />
        <Mtds.Card
          as={Mtds.Flex}
          data-align='center'
          data-justify='space-between'
        >
          <Mtds.Info data-variant='circle'>
            <UserIcon />
            <strong>Username</strong>
            {accountName ?? ''}
          </Mtds.Info>
          <Mtds.Button data-variant='secondary' onClick={logout}>
            Log out
          </Mtds.Button>
        </Mtds.Card>
        <Mtds.Heading as='h2' data-size='sm'>
          Lab settings
        </Mtds.Heading>
        <p>These settings apply to the entire lab and affect all users.</p>
        {notificationEmails !== null && notificationEmails !== undefined && (
          <Mtds.Card>
            <Mtds.Heading as='h3' data-size='sm'>
              Notification emails
            </Mtds.Heading>
            <p>
              Receive an email notification when new analysis type combinations
              are ready for approval.
            </p>
            {emails.length === 0 && <p>No notification emails configured.</p>}
            {emails.map((email) => (
              <Mtds.Flex
                key={email}
                data-align='center'
                data-justify='space-between'
              >
                <span>{email}</span>
                <Mtds.Button
                  data-variant='tertiary'
                  data-size='sm'
                  aria-busy={removeEmail.isPending}
                  disabled={removeEmail.isPending}
                  onClick={() => removeEmail.mutate(email)}
                >
                  Remove
                </Mtds.Button>
              </Mtds.Flex>
            ))}
            {canAddMore && (
              <Mtds.Flex data-direction='row' data-align='end' data-gap='sm'>
                <Mtds.Field
                  className='grow'
                  as='input'
                  type='email'
                  label='Add email address'
                  value={emailInput}
                  onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
                    setEmailInput(target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent) =>
                    e.key === 'Enter' && handleAddEmail()
                  }
                  placeholder='email@example.com'
                  maxLength={256}
                />
                <Mtds.Button
                  data-variant='secondary'
                  aria-busy={addEmail.isPending}
                  disabled={addEmail.isPending}
                  onClick={handleAddEmail}
                >
                  Add email
                </Mtds.Button>
              </Mtds.Flex>
            )}
            <SaveError error={addEmail.error} action='add the email address'>
              Your email address has been kept. Check it and try again.
            </SaveError>
            <SaveError
              error={removeEmail.error}
              action='remove the email address'
            />
            <p>
              <small>
                By entering your email address, you accept that Mattilsynet
                stores it for the purpose of sending you notifications about new
                combinations ready for approval.
              </small>
            </p>
          </Mtds.Card>
        )}
        {currentUser?.labId != null && (
          <Mtds.Group>
            <LabReceptionAddressesSection labId={currentUser.labId} />
          </Mtds.Group>
        )}
      </Mtds.Prose>
    </AppLayout>
  )
}

export default SettingsScreen
